import React, { useMemo, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { v4 as uuid } from "uuid";
import dayjs from "dayjs";

import { formatStartDateEndDate } from "../../../utils/utils";
import DateRangePickerField from "../../../components/form-fields/date-range-picker/date-range-picker";
import TextInputField from "../../../components/form-fields/text-input-field/text-input-field";

// Components & Utils
import { MilestonesStyled, MilestonesListStyled } from "./milestone.style";
import { Typography } from "@mui/material";
import { formatNumber } from "../../../utils/utils";
import SortableAccordian from "../../../components/accordian/SortableAccordian";
import {
  getIsTimeLoggedAgainstMilestone,
  DATE_FORMATE,
} from "./milestone.utils";
import {
  MilestoneHeader,
  MilestoneHeaderFixedProject,
  MilestoneHeaderFixedDeliv,
} from "./milestone-header/MilestoneHeader";
import MilestoneBody from "./milestone-body/MilestoneBody";
import { ALERT_MESSAGES } from "../planner.constants";

const MilestoneList = ({
  milestones = [],
  resources,
  setPlannerState,
  project,
  newMilestoneRef,
  handleAddMilestone,
  serviceTotal,
  handleDeliverableUpdate,
  handleStatusChange,
  ...props
}) => {
  const milestonesArray = Array.isArray(milestones) ? milestones : [];
  const plannerType = project.projectData.plannerType;
  const { plannerStartDate, plannerEndDate } = project.projectData;
  const isServiceValueDisabled =
    plannerType === "Fixed Fee Project" &&
    (!resources || !resources?.[0]?.rate || resources.length === 0);

  // parse the stored plannerStartDate/plannerEndDate into dayjs objects
  const defaultPlannerRange = useMemo(() => {
    if (!plannerStartDate || !plannerEndDate) return [];
    return [
      dayjs(plannerStartDate, "MM-DD-YYYY"),
      dayjs(plannerEndDate, "MM-DD-YYYY"),
    ];
  }, [plannerStartDate, plannerEndDate]);

  const handlePlannerRangeChange = (dates, dateStrings) => {
    const [start, end] = dateStrings;
    setPlannerState((prev) => ({
      ...prev,
      projectData: {
        ...prev.projectData,
        plannerStartDate: start,
        plannerEndDate: end,
      },
    }));
  };

  const handleServiceValueChange = (e) => {
    const value = e.target.value;

    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setPlannerState((prev) => ({
        ...prev,
        projectData: {
          ...prev.projectData,
          plannerServiceValue: e.target.value || 0,
        },
      }));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = milestones.findIndex((m) => m.id === active.id);
    const newIndex = milestones.findIndex((m) => m.id === over.id);

    const updatedMilestones = arrayMove(milestones, oldIndex, newIndex);

    setPlannerState((prev) => ({
      ...prev,
      milestones: updatedMilestones,
    }));
  };

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={milestonesArray.map((m) => m.id)}
        strategy={verticalListSortingStrategy}
      >
        <MilestonesListStyled>
          {plannerType === "Fixed Fee Project" ? (
            <div className="services-fixed">
              <Typography variant="h5">Services</Typography>
              <div className="field">
                <div className="subfield">
                  <Typography variant="body2" className="label">
                    Project Duration:
                  </Typography>

                  <DateRangePickerField
                    key={`${plannerStartDate}-${plannerEndDate}`}
                    defaultValue={defaultPlannerRange}
                    onChange={handlePlannerRangeChange}
                  />
                </div>
                <div className="subfield">
                  <Typography variant="body2" className="label">
                    Service Value:
                  </Typography>
                  <TextInputField
                    value={serviceTotal}
                    InputProps={{
                      startAdornment: <div>$</div>,
                    }}
                    disabled={isServiceValueDisabled}
                    onChange={(e) => {
                      const projectData = project?.projectData;
                      const hasBudget =
                        Array.isArray(project?.milestones) &&
                        project.milestones.some(
                          (milestone) => milestone?.budget > 0
                        );
                      const hasPlannerServiceValue =
                        !!projectData?.plannerServiceValue;

                      if (
                        (hasBudget || hasPlannerServiceValue) &&
                        projectData.status !== "Draft"
                      )
                        handleStatusChange("Draft");
                      handleServiceValueChange(e);
                    }}
                    placeholder="0.00"
                    sx={{ width: "100px" }}
                    inputType={"number"}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="services">
              <Typography variant="h5">Services</Typography>
              <Typography variant="h5">
                $ {formatNumber(serviceTotal)}
              </Typography>
            </div>
          )}
          {milestonesArray.map((milestone, index) => (
            <Milestone
              key={milestone.id}
              milestone={milestone}
              milestoneIndex={index}
              newMilestoneRef={newMilestoneRef}
              project={project}
              resources={resources}
              setPlannerState={setPlannerState}
              handleAddMilestone={handleAddMilestone}
              handleDeliverableUpdate={handleDeliverableUpdate}
              handleStatusChange={handleStatusChange}
              {...props}
            />
          ))}
        </MilestonesListStyled>
      </SortableContext>
    </DndContext>
  );
};

const Milestone = ({
  milestone,
  resources,
  milestones = [],
  newMilestoneRef,
  milestoneIndex,
  handleMilestoneChange,
  handleDeleteMilestone,
  handleTaskChange,
  handleDeleteTask,
  project,
  loggedInstances,
  isGeneralUser,
  handleDeliverableUpdate,
  handleStatusChange,
}) => {
  const [expanded, setExpanded] = useState([""]);

  const isTimeLoggedAgainstMilestone = useMemo(
    () => getIsTimeLoggedAgainstMilestone(loggedInstances, milestone),
    [loggedInstances, milestone]
  );

  const handleDateRangeChange = (e, milestoneIndex, taskIndex) => {
    const formattedDates = formatStartDateEndDate(e, DATE_FORMATE);
    handleTaskChange(
      milestoneIndex,
      taskIndex,
      "startDate",
      formattedDates?.[0]
    );
    handleTaskChange(milestoneIndex, taskIndex, "endDate", formattedDates?.[1]);
  };

  const onDeleteMilestone = () => {
    const isValueAllocated =
      milestone?.hours > 0 ||
      milestone?.budget > 0 ||
      isTimeLoggedAgainstMilestone;

    if (isValueAllocated) {
      window.alert(ALERT_MESSAGES.DeleteDeliveravle);
      return;
    }

    handleDeleteMilestone(milestoneIndex);
  };

  const handleAddTask = () =>
    handleMilestoneChange(milestoneIndex, "tasks", [
      ...milestone.tasks,
      {
        description: "",
        organization: "FKA",
        startDate: "",
        endDate: "",
        hours: 0,
        budget: 0,
        id: uuid(),
        resources: [
          {
            hours: 0,
            id: uuid(),
            isEnabled: false,
          },
        ],
      },
    ]);

  const handleAccordionChange = (panel) => {
    setExpanded((prevExpanded) =>
      prevExpanded.includes(panel)
        ? prevExpanded.filter((p) => p !== panel)
        : [...prevExpanded, panel]
    );
  };
  const getMilestoneHeader = () => {
    if (project.projectData.plannerType === "Fixed Fee Project") {
      return (
        <MilestoneHeaderFixedProject
          milestone={milestone}
          isGeneralUser={isGeneralUser}
          milestoneIndex={milestoneIndex}
          onDeleteMilestone={onDeleteMilestone}
          handleMilestoneChange={handleMilestoneChange}
          hideLabels={!expanded.includes("deliverable")}
        />
      );
    } else if (project.projectData.plannerType === "Fixed Fee Deliverable") {
      return (
        <MilestoneHeaderFixedDeliv
          project={project}
          milestone={milestone}
          isGeneralUser={isGeneralUser}
          milestoneIndex={milestoneIndex}
          resources={resources}
          onDeleteMilestone={onDeleteMilestone}
          handleMilestoneChange={handleMilestoneChange}
          hideLabels={!expanded.includes("deliverable")}
          handleStatusChange={handleStatusChange}
        />
      );
    } else {
      // Time + Materials or any other type falls back to the default header
      return (
        <MilestoneHeader
          milestone={milestone}
          isGeneralUser={isGeneralUser}
          milestoneIndex={milestoneIndex}
          onDeleteMilestone={onDeleteMilestone}
          handleMilestoneChange={handleMilestoneChange}
          hideLabels={!expanded.includes("deliverable")}
        />
      );
    }
  };

  const getBody = () => {
    if (project.projectData.plannerType === "Time + Materials") {
      return (
        <MilestoneBody
          project={project}
          milestone={milestone}
          resources={resources}
          builderMilestoneTasks={milestone.tasks || []}
          isGeneralUser={isGeneralUser}
          handleDeleteTask={handleDeleteTask}
          handleTaskChange={handleTaskChange}
          milestoneIndex={milestoneIndex}
          handleDateRangeChange={handleDateRangeChange}
          handleMilestoneChange={handleMilestoneChange}
          handleDeliverableUpdate={handleDeliverableUpdate}
          handleAddTask={handleAddTask}
          handleStatusChange={handleStatusChange}
        />
      );
    }
    return null;
  };

  return (
    <MilestonesStyled
      key={milestone.id}
      ref={milestoneIndex === milestones.length - 1 ? newMilestoneRef : null}
      isGeneralUser={isGeneralUser}
    >
      <SortableAccordian
        id={milestone.id}
        name={"deliverable"}
        handleChange={() => handleAccordionChange("deliverable")}
        expanded={expanded.includes("deliverable") ? "deliverable" : ""}
        variant="purple"
        arrowPosition="right"
        heading={getMilestoneHeader()}
        body={getBody()}
      />
    </MilestonesStyled>
  );
};

export default MilestoneList;
